/**
 * Per-document signature capability + state, computed by the backend for the current user
 * against the current submission. The frontend should consume `allowed*SignatureActions`
 * directly to decide whether to render Sign / Unsign / Edit / Reassign / etc. buttons,
 * rather than deriving capability from raw `*_sign_status` enums.
 *
 * Mirrors `com.restore.core.dto.signature.FormSubmissionSignatureContext` on the backend.
 */

/**
 * Action names mirror the backend's {@code com.restore.core.dto.signature.SignatureAction}
 * enum. Use the {@link SignatureAction} constants at call sites rather than hardcoded
 * strings so renames / typos are caught at compile time:
 *
 *   if (canDo(ctx.allowedTherapistSignatureActions, SignatureAction.CAN_SIGN)) { ... }
 */
export const SignatureAction = {
  CAN_SIGN: 'CAN_SIGN',
  CAN_UNSIGN: 'CAN_UNSIGN',
  CAN_EDIT: 'CAN_EDIT',
  CAN_REASSIGN: 'CAN_REASSIGN',
  CAN_SIGN_ON_BEHALF: 'CAN_SIGN_ON_BEHALF',
  CAN_SELECT_SUPERVISOR: 'CAN_SELECT_SUPERVISOR',
  CAN_SELECT_PHYSICIAN: 'CAN_SELECT_PHYSICIAN',
  //  BRK-2057: compliance on-behalf proxy — permission-gated (COMPLIANCE_SIGN_ON_BEHALF),
  //  emitted on the therapist and supervisor axes; independent of the DOR-only
  //  CAN_SIGN_ON_BEHALF above.
  CAN_COMPLIANCE_SIGN_ON_BEHALF: 'CAN_COMPLIANCE_SIGN_ON_BEHALF',
  //  BRK-2072: marker that this axis's CAN_UNSIGN/CAN_EDIT come from the
  //  COMPLIANCE_DOCUMENT_ON_BEHALF grant rather than authorship/assignment — the FE forks into
  //  the mandatory reason+consent flow when present. Never emitted alone.
  CAN_COMPLIANCE_DOCUMENT_ON_BEHALF: 'CAN_COMPLIANCE_DOCUMENT_ON_BEHALF',
  //  BRK-2085: the COMPLIANCE_SIGN_ON_BEHALF holder may fill/swap this cosigner slot
  //  (unblocking their own proxy sign). Distinct from the ungated author/DOR CAN_SELECT_* so
  //  the affordance can sit behind the epic kill switch + scope check.
  CAN_COMPLIANCE_SELECT_SUPERVISOR: 'CAN_COMPLIANCE_SELECT_SUPERVISOR',
  CAN_COMPLIANCE_SELECT_PHYSICIAN: 'CAN_COMPLIANCE_SELECT_PHYSICIAN',
} as const;
export type SignatureAction = (typeof SignatureAction)[keyof typeof SignatureAction];

export interface SignerSummary {
  id: string | null;
  name: string | null;
  signStatus: string | null;
  signedAt: string | null;
}

export interface FormSubmissionSignatureContext {
  allowedTherapistSignatureActions: SignatureAction[];
  allowedSupervisorSignatureActions: SignatureAction[];
  allowedPhysicianSignatureActions: SignatureAction[];
  therapist: SignerSummary;
  supervisor: SignerSummary;
  physician: SignerSummary;
  supervisorSignatureRequired: boolean;
  physicianSignatureRequired: boolean;
}

/**
 * True when the given action is present in the action list. Lets callers write
 * `canDo(ctx?.allowedSupervisorSignatureActions, SignatureAction.CAN_SIGN)` instead of
 * repeating the optional-chaining `?.includes(...)` pattern at every site.
 */
export function canDo(
  actions: SignatureAction[] | null | undefined,
  action: SignatureAction
): boolean {
  return Array.isArray(actions) && actions.includes(action);
}
